import { Component, OnDestroy, Renderer2 } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { MatDialogRef } from "@angular/material/dialog";
import { AuthService } from "@core/auth.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { LoggingService } from "src/app/services/logging.service";

@Component({
  selector: "app-stuck-website",
  templateUrl: "./stuck-website.component.html",
  styleUrls: ["./stuck-website.component.scss"],
  standalone: true,
  imports: [TranslatePipe],
})
export class StuckWebsiteComponent implements OnDestroy {
  private debounceTimeout = 1000;
  private debounceTimer: NodeJS.Timeout | null = null;
  private errorTimer: NodeJS.Timeout | null = null;
  public isLoading = false;
  public userId: string;

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private loggingService: LoggingService,
    private angularFireAuth: AngularFireAuth,
    private dialogRef: MatDialogRef<StuckWebsiteComponent>,
    private renderer: Renderer2
  ) {
    try {
      const language = this.authService?.selectedlanguage$.getValue();
      translate.use(language || "en");
    } catch (error) {
      this.loggingService.logError("Failed to set language", error);
      translate.use("en"); // Fallback to English
    }
  }
  async ngOnInit() {
    await this.authService.getUid("StuckWebsiteComponent").then((uid) => {
      this.userId = uid;
    })
  }
  wait() {
    this.loggingService.logInfo("User chose to wait when app was detected as stuck");
    this.dialogRef.close("wait");
  }

  reload() {
    // Prevent multiple clicks
    if (this.isLoading) return;
    this.isLoading = true;
    // Clear any existing timer to prevent race conditions
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.loggingService.logWarning("User initiated reload due to stuck app.");

    this.debounceTimer = setTimeout(async () => {
      try {
        await this.clearDataAndReload();
      } catch (error) {
        this.loggingService.logError("Failed to clear data and reload", error);
        // Use a translated message instead of alert
        const errorMessage = await firstValueFrom(
          this.translate.get('errors.refresh_failed')
        );
        // Display error in UI rather than using alert
        this.showError(errorMessage || "An error occurred while attempting to refresh the page. Please try again later.");
      } finally {
        this.isLoading = false;
      }
    }, this.debounceTimeout);
  }

  async clearDataAndReload(fullReset = false) {
    const authPreserved = await this.preserveAuthToken();
    if (!authPreserved) {
      this.loggingService.logWarning("Could not preserve authentication. User may need to log in again.");
    }

    try {
      // Clear all caches
      const appCaches = [];
      const cacheNames = await caches.keys();

      this.loggingService.logInfo(`Found ${cacheNames.length} caches: ${cacheNames.join(', ')}`);

      await Promise.all(
        cacheNames
          .filter(cacheName => !appCaches.includes(cacheName))
          .map(cacheName => caches.delete(cacheName))
      );
      const preserveDbs = ["firebaseLocalStorageDb"];

      // Clear all data in IndexedDB except for auth-related databases
      if (indexedDB.databases) {
        const dbs = await indexedDB.databases();
        this.loggingService.logInfo(`Found ${dbs.length} IndexedDB databases`);

        const deletionResults = await Promise.allSettled(
          dbs.map(async (db) => {
            if (db.name && (fullReset || !preserveDbs.includes(db.name))) {
              try {
                await indexedDB.deleteDatabase(db.name);
                this.loggingService.logInfo(`Successfully deleted database: ${db.name}`);
                return { success: true, dbName: db.name };
              } catch (error) {
                this.loggingService.logError(`Failed to delete database ${db.name}`, error);
                return { success: false, dbName: db.name, error };
              }
            }
            return { success: true, dbName: db.name, skipped: true };
          })
        );

        // Check if any critical databases failed to delete
        const failures = deletionResults.filter(
          result => result.status === 'rejected' ||
            (result.status === 'fulfilled' && !result.value.success && !result.value.skipped)
        );

        if (failures.length > 0) {
          this.loggingService.logWarning(`Failed to delete ${failures.length} databases during cleanup`);
          // Don't throw here - continue with reload but log the issue
        }
      }

      // Define keys to preserve or remove based on reset type
      const localStorageKeys = fullReset ? [] : ["fbToken", "firebaseSDKVersion", "firebase_version"];

      // Array to store the keys that need to be removed
      const keysToRemove = [];

      // Identify the keys that need to be removed
      Object.keys(localStorage).forEach((key) => {
        if (!localStorageKeys.includes(key)) {
          keysToRemove.push(key);
        }
      });

      // Remove the identified keys
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      await this.restoreAuthToken();

      // Always unregister SWs so a broken worker cannot re-break the next load
      // after caches were cleared (Refresh and Reset).
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      }

      // Create loading overlay
      this.createLoadingOverlay();

      // Ensure all async operations are complete before reloading
      const url = fullReset ? `${window.location.pathname}?reset=${Date.now()}` : window.location.href;

      // Small delay just for UI feedback, not for operation completion
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = url;

    } catch (error) {
      this.loggingService.logError("Error during clearDataAndReload", error);
      throw error;
    }
  }

  private createLoadingOverlay(): HTMLElement {
    // Remove any existing overlay first
    const existingOverlay = document.getElementById('reload-overlay');
    if (existingOverlay) {
      existingOverlay.parentNode?.removeChild(existingOverlay);
    }

    const loadingEl = this.renderer.createElement('div');
    this.renderer.setAttribute(loadingEl, 'id', 'reload-overlay');
    this.renderer.setStyle(loadingEl, 'position', 'fixed');
    this.renderer.setStyle(loadingEl, 'top', '0');
    this.renderer.setStyle(loadingEl, 'left', '0');
    this.renderer.setStyle(loadingEl, 'width', '100%');
    this.renderer.setStyle(loadingEl, 'height', '100%');
    this.renderer.setStyle(loadingEl, 'background', 'rgba(0,0,0,0.7)');
    this.renderer.setStyle(loadingEl, 'display', 'flex');
    this.renderer.setStyle(loadingEl, 'justify-content', 'center');
    this.renderer.setStyle(loadingEl, 'align-items', 'center');
    this.renderer.setStyle(loadingEl, 'z-index', '9999');

    const textEl = this.renderer.createElement('h1');
    this.renderer.setStyle(textEl, 'color', 'white');
    const text = this.renderer.createText('Reloading...');
    this.renderer.appendChild(textEl, text);
    this.renderer.appendChild(loadingEl, textEl);
    this.renderer.appendChild(document.body, loadingEl);

    return loadingEl;
  }

  async preserveAuthToken() {
    try {
      if (this.userId) {
        const data = await firstValueFrom(this.authService.getCustomToken(this.userId));
        if (data.success) {
          // Store in sessionStorage instead of localStorage for better security
          localStorage.setItem("fbToken", data.customToken);
          sessionStorage.setItem("fbToken", data.customToken);
          return true;
        }
      }
      return false;
    } catch (error) {
      this.loggingService.logError("Error preserving auth token", error);
      return false;
    }
  }

  async restoreAuthToken() {
    try {
      const authToken = sessionStorage.getItem("fbToken") || localStorage.getItem("fbToken");
      if (authToken) {
        await this.angularFireAuth.signInWithCustomToken(authToken);
        // Clear the temporary token immediately after use
        sessionStorage.removeItem("fbToken");
        return true;
      }
      return false;
    } catch (error) {
      this.loggingService.logError("Error restoring auth token", error);
      return false;
    }
  }

  private showError(message: string) {
    // Clear any existing error timer
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }

    // Remove any existing error message first
    const existingError = document.querySelector('.error-message');
    if (existingError) {
      existingError.parentNode?.removeChild(existingError);
    }

    // Create a more user-friendly error display
    const errorContainer = this.renderer.createElement('div');
    this.renderer.addClass(errorContainer, 'error-message');
    this.renderer.setStyle(errorContainer, 'color', '#ff4d4d');
    this.renderer.setStyle(errorContainer, 'margin-bottom', '15px');
    this.renderer.setStyle(errorContainer, 'text-align', 'center');
    const textNode = this.renderer.createText(message);
    this.renderer.appendChild(errorContainer, textNode);

    // Find the update button to insert before it
    const button = document.querySelector('.update-btn');
    const parent = button?.parentNode;

    if (parent) {
      this.renderer.insertBefore(parent, errorContainer, button);

      // Auto-remove after 10 seconds with proper timer tracking
      this.errorTimer = setTimeout(() => {
        if (errorContainer.parentNode) {
          this.renderer.removeChild(parent, errorContainer);
        }
        this.errorTimer = null;
      }, 10000);
    }
  }

  reset() {
    this.isLoading = true;
    this.loggingService.logWarning("App appears to be stuck. User chose full reset.");
    this.clearDataAndReload(true)
      .finally(() => {
        this.isLoading = false;
      })
      .catch((error) => {
        this.loggingService.logError("Failed to reset app", error);
        alert("An error occurred while attempting to reset the app. Please try again later.");
      });
  }

  ngOnDestroy() {
    // Cleanup timers on component destruction
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }
}
